import { Button, Stack, Table, Text, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { queryOptions, useSuspenseInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { LoadMoreTrigger } from "shared/components/global/LoadMoreTrigger";
import { useSearch } from "shared/hooks/useSearch";
import { useAside } from "shared/utils/aside";
import { qk } from "shared/utils/qk";
import { createOffsetBasedGqlQueryOptions } from "shared/utils/query";
import { zDate } from "shared/utils/zod";
import { graphql } from "src/gql";

const Page = () => {
    const aside = useAside();

    const { search, bindSearchProps } = useSearch();
    const { data } = useSuspenseInfiniteQuery(queries.table(search));

    return (
        <Content paper>
            <Content.Heading backable>Do Not Send List (SMS Opt Out)</Content.Heading>
            <Content.Action>
                <TextInput variant="filled" leftSection={<IconSearch size={16} />} placeholder="Search..." {...bindSearchProps()} />
            </Content.Action>

            <Table>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>User's Number</Table.Th>
                        <Table.Th>From Number</Table.Th>
                        <Table.Th>Opted Out At</Table.Th>
                        <Table.Th />
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {data.pages.flat().map((item) => (
                        <Table.Tr key={item.id}>
                            <Table.Td>{item.recipientPhoneNumber}</Table.Td>
                            <Table.Td>{item.senderPhoneNumber}</Table.Td>
                            <Table.Td>{zDate.parse(item.createdAt).toLocaleString()}</Table.Td>
                            <Table.Td>
                                <Button
                                    variant="transparent"
                                    size="compact-xs"
                                    td="underline"
                                    onClick={() =>
                                        aside.present({
                                            label: `Opt Out: ${item.senderPhoneNumber}/${item.recipientPhoneNumber}`,
                                            component: (
                                                <Suspense>
                                                    <Details
                                                        recipientPhoneNumber={item.recipientPhoneNumber!}
                                                        senderPhoneNumber={item.senderPhoneNumber}
                                                    />
                                                </Suspense>
                                            ),
                                            mode: "drawer",
                                        })
                                    }
                                >
                                    More
                                </Button>
                            </Table.Td>
                        </Table.Tr>
                    ))}
                    <LoadMoreTrigger query={queries.table(search)} />
                </Table.Tbody>
            </Table>
        </Content>
    );
};

/** @private */
const Details = (props: { senderPhoneNumber: string; recipientPhoneNumber: string }) => {
    const { data } = useSuspenseQuery(queries.details(props));

    return (
        <Stack>
            <Text fw="bold">EUDs</Text>
            <Table withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Customer</Table.Th>
                        <Table.Th>Owner</Table.Th>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Verified</Table.Th>
                        <Table.Th>
                            <abbr title="Enabled for informational messages">Default</abbr>
                        </Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {data.support.smsOptOut.euds.map((eud) => (
                        <Table.Tr key={eud.id}>
                            <Table.Td>{eud.customer.name}</Table.Td>
                            <Table.Td>
                                <div>
                                    {eud.user.name}
                                    <Text size="xs" c="dimmed">
                                        {eud.user.loginEmail}
                                    </Text>
                                </div>
                            </Table.Td>
                            <Table.Td>{eud.name ?? "None"}</Table.Td>
                            <Table.Td>{eud.isVerified ? "Yes" : "No"}</Table.Td>
                            <Table.Td>{eud.isDefault ? "Yes" : "No"}</Table.Td>
                        </Table.Tr>
                    ))}
                </Table.Tbody>
            </Table>
        </Stack>
    );
};

const queries = {
    table: (q: string) =>
        createOffsetBasedGqlQueryOptions({
            queryKey: qk("support", "do-not-send-list", "search", q),
            document: graphql(`
                query DevtoolsDoNotSendList($limit: Int!, $offset: Long!, $q: String!) {
                    support {
                        smsOptOuts(limit: $limit, offset: $offset, query: $q) {
                            id
                            createdAt
                            recipientPhoneNumber
                            senderPhoneNumber
                        }
                    }
                }
            `),
            select: (node) => node.support.smsOptOuts,
            variables: { q },
        }),
    details: (opts: { senderPhoneNumber: string; recipientPhoneNumber: string }) =>
        queryOptions({
            queryKey: qk("support", "do-not-send-list", "id", Object.values(opts).join(":")),
            queryFn: () =>
                httpPostGraphql(
                    graphql(`
                        query DevtoolsDoNotSendDetails($senderPhoneNumber: String!, $recipientPhoneNumber: String!) {
                            support {
                                smsOptOut(recipientPhoneNumber: $recipientPhoneNumber, senderPhoneNumber: $senderPhoneNumber) {
                                    id
                                    euds {
                                        customer {
                                            name
                                        }
                                        id
                                        isDefault
                                        isVerified
                                        name
                                        type
                                        value
                                        user {
                                            id
                                            name
                                            loginEmail
                                        }
                                    }
                                }
                            }
                        }
                    `),
                    opts,
                ),
        }),
};

/** @public */
export const Route = createFileRoute("/_auth/_dev/devtools/do-not-send-list")({
    component: Page,
});
